import React, { useRef, useCallback, useMemo, useEffect, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";

import COMMON from "common";
import api from "services/api";
import sanitizeObject from "common/sanitize-object";
import { formatDatePattern } from "common/calendar";
import serveRequestErrors from "common/serve-request-errors";
import { formatCurrency } from "common/format-currency-pattern";
import AppStatus from "components/app-status";
import AppEmptyState from "components/app-empty-state";
import AppSearchInput from "components/app-search-input";
import AppOrderModal from "components/pages/orders/app-order-modal";
import AppTable, { AppTableEditIcon, AppTableCell } from "components/app-table";
import viewIcon from "assets/images/view-icon.svg";

const PageOrders = (props) => {
	const orderModalRef = useRef();
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	const paramsRef = useRef({ page: parseInt(searchParams.get("page")) || 1, sort: searchParams.get("sort") || "", orderNo: searchParams.get("orderNo") || "" });
	const [data, setData] = useState({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });
	const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
	const isEmptyState = useMemo(() => COMMON.TABLE_REQUEST_STATUS.INSTANCE === data.status && !paramsRef.current.orderNo, [data.status]);

	const onHandleGetList = useCallback(async () => {
		let response = null;

		setData({ page: paramsRef.current.page, size: 10, total: 0, offset: 0, totalElements: 0, prev: false, next: false, items: [] });

		try {
			const payload = { ...paramsRef.current, size: 10 };

			memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

			payload.page = paramsRef.current.page - 1;

			response = await api.get.orders.list(sanitizeObject(payload));
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) {
			setData((prev) => ({
				...prev,
				page: paramsRef.current.page,
				prev: !response.first,
				next: !response.last,
				items: response.content,
				total: response.totalPages,
				totalElements: response.totalElements,
				offset: response?.pageable?.offset || 0,
				status: response?.pageable,
			}));
		}
	}, []);

	//prettier-ignore
	const onHandlePagination = useCallback((event) => {
		const control = event.currentTarget?.getAttribute("data-ctrl");

		if (control) {
			if (control === "prev") {
				if (paramsRef.current.page <= 1) return;
				paramsRef.current.page -= 1;
			} else {
				if (paramsRef.current.page >= data.total) return;
				paramsRef.current.page += 1;
			}
		} else {
			paramsRef.current.page = event.target.value;
		}

		onHandleGetList();
	}, [onHandleGetList, data.total]);

	//prettier-ignore
	const onHandleSearch = (event) => {
		const value = event.target.value;
		paramsRef.current.orderNo = value;
		onHandleGetList();
	};

	const onHandleDebounceSearch = debounce(onHandleSearch, 1000);

	//prettier-ignore
	const onHandleSort = useCallback((id, order) => {
		paramsRef.current.sort = order ? id + "," + order : "";

		paramsRef.current.page = 1;

		onHandleGetList();
	}, [onHandleGetList]);

	//prettier-ignore
	const onHandleViews = useCallback((id) => {
		orderModalRef.current.onHandleShow(id);
	}, []);

	//prettier-ignore
	const MenuCell = useCallback(({ row }) => {
        return <AppTableEditIcon icon={viewIcon} onClick={() => onHandleViews(row.original.id)} />;
    }, [onHandleViews]);

	const CreateDateCell = useCallback(({ row }) => {
		const date = formatDatePattern(row.original.createdDate);
		return <AppTableCell left value={date} />;
	}, []);

	const PremiumAmountCell = useCallback(({ row }) => <AppTableCell right value={"RM " + formatCurrency(row.original.annualPremiumPrice)} />, []);

	//prettier-ignore
	const StatusCell = useCallback(({ row }) => {
		return <AppStatus status={row.values.orderStatus} />;
	}, []);

	//prettier-ignore
	const columns = useMemo(() => [
		{
			Header: "Ref. No.",
			accessor: "orderNo",
			disableSortBy: false,
		},
		{
			Header: "Plan Name",
			accessor: "productName",
			disableSortBy: false,
		},
		{
			Header: "Benefit Type",
			accessor: "benefitTypes",
			disableSortBy: true,
		},
		{
			Header: "Order Submission Date",
			accessor: "createdDate",
			disableSortBy: false,
			Cell: CreateDateCell
		},
		{
			Header: "Premium Amount",
			accessor: "annualPremiumPrice",
			disableSortBy: true,
			Cell: PremiumAmountCell
		},
		{
			Header: "Status",
			accessor: "orderStatus",
			disableSortBy: true,
			Cell: StatusCell
		},
		{
			Header: "",
			accessor: "*",
			disableSortBy: true,
			Cell: MenuCell,
		},
	], [MenuCell, CreateDateCell, PremiumAmountCell, StatusCell]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.ORDERS.LIST);
		};
	}, [cancelRequest]);

	return (
		<div className="page-orders">
			<div className="orders">
				{isEmptyState && <AppEmptyState title="No records found" description="You don’t have any records yet" disabledButton />}

				{!isEmptyState && (
					<Fragment>
						<AppSearchInput defaultValue={paramsRef.current.orderNo} onChange={onHandleDebounceSearch} />

						{paramsRef.current.orderNo && (
							<div className="orders__results">
								<p className="orders__text">{data.totalElements} results found</p>
							</div>
						)}
						
						<AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
					</Fragment>
				)}
			</div>

			<AppOrderModal ref={orderModalRef} />
		</div>
	);
};

export default PageOrders;
